import { createResource, createSignal, createMemo, For, Show, type JSX } from 'solid-js';
import { A } from '@solidjs/router';
import {
  fetchFeaturedReviews,
  fetchMyReviewVotes,
  voteForReview,
  unvoteReview,
} from '@lib/supabase/reviews';
import { authStore } from '@stores/session';
import ReviewImageGallery from '@components/reviews/ReviewImageGallery';

export default function StrainReviewsSection(props: {
  strainId: string;
  strainSlug: string;
}): JSX.Element {
  const [reviews, { mutate: mutateReviews }] = createResource(
    () => props.strainId,
    fetchFeaturedReviews,
  );
  // Source key is the sorted comma-joined review id list — stable
  // string instead of a fresh array every reactive tick. Solid's
  // resource source comparison is `===`, and arrays are reference-
  // compared, so a fresh array would refetch on every render.
  const [myVotes, { mutate: mutateVotes }] = createResource(
    () => (reviews() ?? []).map((r) => r.id).sort().join(','),
    async (key) => fetchMyReviewVotes(key ? key.split(',') : []),
  );
  const [busy, setBusy] = createSignal<string | null>(null);

  const hasVoted = (id: string) => (myVotes() ?? new Set<string>()).has(id);

  // Mutate both signals locally rather than refetching — we know the
  // exact post-action state for both (vote membership + the review's
  // upvote count), so a server round-trip just to re-derive the same
  // values would just flash the resource into a pending state.
  async function toggleVote(reviewId: string) {
    if (!authStore.user()) return;
    const wasVoted = hasVoted(reviewId);
    setBusy(reviewId);
    try {
      if (wasVoted) await unvoteReview(reviewId);
      else await voteForReview(reviewId);
      mutateVotes((prev) => {
        const next = new Set(prev ?? []);
        if (wasVoted) next.delete(reviewId);
        else next.add(reviewId);
        return next;
      });
      mutateReviews((prev) =>
        prev?.map((r) => {
          if (r.id !== reviewId) return r;
          // Mutate the review object IN PLACE so <For> doesn't see a
          // new reference and unmount the row (which would lose the
          // button focus mid-click). Returning the same r reference
          // keeps the list stable; reactivity fires via the new array.
          r.upvotes = r.upvotes + (wasVoted ? -1 : 1);
          return r;
        }) ?? prev,
      );
    } finally {
      setBusy(null);
    }
  }

  const list = createMemo(() => reviews() ?? []);

  return (
    <section class="mt-10">
      <div class="flex items-center justify-between">
        <h2 class="font-display text-xl font-semibold">Featured Reviews</h2>
        <Show when={authStore.user()}>
          <A
            href={`/forum?category=reviews&strain=${encodeURIComponent(props.strainSlug)}`}
            class="text-xs rounded-full border border-brand-hex px-3 py-1 hover:bg-brand-hex/40"
          >
            + Write a review
          </A>
        </Show>
      </div>

      <Show when={!reviews.loading} fallback={<p class="mt-3 text-sm">Loading…</p>}>
        <Show
          when={list().length > 0}
          fallback={<p class="mt-3 text-sm text-brand-green/60">No reviews yet &ndash; be the first.</p>}
        >
          <ul class="mt-3 space-y-3">
            <For each={list()}>
              {(r) => (
                <li class="rounded-xl border border-brand-hex bg-white p-4">
                  <Show when={r.images && r.images.length > 0}>
                    <div class="mb-3">
                      <ReviewImageGallery images={r.images} />
                    </div>
                  </Show>
                  <div class="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div class="flex items-center gap-2">
                        <Show when={r.is_featured}>
                          <span class="text-[10px] rounded-full bg-brand-moss text-brand-cream px-2 py-0.5 uppercase tracking-wide">
                            Featured
                          </span>
                        </Show>
                        <span class="font-display text-2xl font-bold">
                          {Number(r.overall).toFixed(1)}
                        </span>
                        <span class="text-xs text-brand-green/60">/ 10 overall</span>
                      </div>
                      <div class="mt-2 grid grid-cols-5 gap-2 text-[11px] text-brand-green/80 max-w-md">
                        <Rating label="Look" value={r.appearance} />
                        <Rating label="Aroma" value={r.aroma} />
                        <Rating label="Taste" value={r.taste} />
                        <Rating label="Effects" value={r.effects} />
                        <Rating label="Price" value={r.price} />
                      </div>
                      <div class="mt-2 flex items-center gap-2 text-[11px] text-brand-green/60 flex-wrap">
                        <Show when={r.format}>
                          <span class="capitalize">{r.format}</span>
                        </Show>
                        <Show when={r.pharmacy_slug}>
                          <span class="capitalize">· {r.pharmacy_slug}</span>
                        </Show>
                        <Show when={r.batch_number}>
                          <span>· Batch #{r.batch_number}</span>
                        </Show>
                        <Show when={r.price_paid_gbp != null}>
                          <span>· £{r.price_paid_gbp?.toFixed(2)}</span>
                        </Show>
                        <Show when={r.would_reorder != null}>
                          <span>· {r.would_reorder ? 'Would reorder' : "Wouldn't reorder"}</span>
                        </Show>
                      </div>
                      <Show when={
                        r.appearance_notes || r.aroma_notes || r.taste_notes
                          || r.effects_notes || r.price_notes || r.other_notes
                      }>
                        <dl class="mt-3 space-y-2 text-xs max-w-prose">
                          <NoteRow label="Appearance" value={r.appearance_notes} />
                          <NoteRow label="Aroma"      value={r.aroma_notes} />
                          <NoteRow label="Taste"      value={r.taste_notes} />
                          <NoteRow label="Effects"    value={r.effects_notes} />
                          <NoteRow label="Price"      value={r.price_notes} />
                          <NoteRow label="Other"      value={r.other_notes} />
                        </dl>
                      </Show>
                    </div>
                    <button
                      type="button"
                      disabled={busy() === r.id || !authStore.user()}
                      onClick={() => toggleVote(r.id)}
                      class={`shrink-0 rounded-full text-xs px-3 py-1.5 border ${
                        hasVoted(r.id)
                          ? 'bg-brand-moss text-brand-cream border-brand-moss'
                          : 'bg-white text-brand-green border-brand-hex hover:bg-brand-hex/40'
                      } disabled:opacity-50`}
                    >
                      ▲ {r.upvotes}
                    </button>
                  </div>
                  <A
                    href={`/forum?post=${r.forum_post_id}`}
                    class="mt-3 inline-block text-xs text-brand-moss hover:underline"
                  >
                    Read full review →
                  </A>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
    </section>
  );
}

function Rating(props: { label: string; value: number }): JSX.Element {
  return (
    <div class="flex flex-col items-center rounded-md bg-brand-hex/40 px-1 py-1">
      <span class="text-[9px] uppercase tracking-wide text-brand-green/60">{props.label}</span>
      <span class="font-display font-semibold">{props.value}</span>
    </div>
  );
}

function NoteRow(props: { label: string; value: string | null }): JSX.Element {
  return (
    <Show when={props.value}>
      <div class="flex gap-2">
        <dt class="font-semibold shrink-0 w-20 text-brand-green/80">{props.label}</dt>
        <dd class="text-brand-green/90 whitespace-pre-wrap">{props.value}</dd>
      </div>
    </Show>
  );
}
